import React, { useCallback, type ReactNode } from 'react';
import type { InstitutionalHoldersFileResponse } from '@/services/api';
import type {
  FilingPeriodForChart,
  QuarterEndChartReference,
} from '@/utils/institutionalHoldersChartData';
import type { InstitutionalHoldersChartDisplayMode } from '@/utils/institutionalHoldersChartDisplayMode';
import {
  INSTITUTIONAL_HOLDERS_RIBBON_HEIGHT_PX,
  InstitutionalHoldersColumn,
  columnCenterPlotX,
} from '@/components/InstitutionalHoldersChartRibbon';
import {
  layoutForQuarterColumn,
  overlayUsesMinimalDensity,
  quarterColumnSlotWidthPx,
} from '@/utils/institutionalHoldersChartDensity';

type ChartMetrics = {
  xAxisMap?: Record<string, { scale?: (v: string) => number; bandwidth?: () => number }>;
  offset?: { left: number; top: number; width: number; height: number };
};

const XHTML_NS = 'http://www.w3.org/1999/xhtml';

const foreignObjectDivProps = { xmlns: XHTML_NS } as React.HTMLAttributes<HTMLDivElement> & {
  xmlns: string;
};

export interface InstitutionalHoldersOverlayMeta {
  usesMinimalDensity: boolean;
}

/**
 * Quarter-end reference lines + holder columns anchored in the chart SVG
 * (foreignObject) so x positions match the price scale exactly.
 */
export function useInstitutionalHoldersChartOverlay(
  enabled: boolean,
  filingPeriods: FilingPeriodForChart[],
  quarterEndReferenceLines: QuarterEndChartReference[],
  holdersPayload: InstitutionalHoldersFileResponse | null,
  stockSymbol: string,
  visibleChartStartDate?: string | null,
  loading = false,
  onOverlayMeta?: (meta: InstitutionalHoldersOverlayMeta) => void,
  displayMode: InstitutionalHoldersChartDisplayMode = 'activity',
  searchHighlight: { quarterKey: string; positionKey: string } | null = null,
  searchFilterActive = false
) {
  const quarterEndChartDatesAsc = quarterEndReferenceLines.map((line) => line.chartDate);
  return useCallback(
    (chartProps: ChartMetrics & Record<string, unknown>) => {
      if (!enabled) return null;

      const xAxisMap = chartProps.xAxisMap;
      const offset = chartProps.offset;
      if (!xAxisMap || !offset) return null;

      const xAxis = xAxisMap[0] ?? Object.values(xAxisMap)[0];
      const xScale = xAxis?.scale;
      if (typeof xScale !== 'function') return null;

      const bw = typeof xAxis.bandwidth === 'function' ? xAxis.bandwidth() : 0;
      const ribbonTop = Math.max(0, offset.top - INSTITUTIONAL_HOLDERS_RIBBON_HEIGHT_PX);
      const ribbonHeight = Math.min(
        INSTITUTIONAL_HOLDERS_RIBBON_HEIGHT_PX,
        Math.max(0, offset.top - 4)
      );
      const listMaxH = Math.max(60, ribbonHeight - 36);

      const slotWidths: number[] = [];
      const nodes: ReactNode[] = [];

      if (loading && filingPeriods.length === 0) {
        nodes.push(
          <foreignObject
            key="inst-loading"
            x={Math.max(0, offset.width / 2 - 80)}
            y={ribbonTop}
            width={160}
            height={ribbonHeight}
          >
            <div
              {...foreignObjectDivProps}
              className="flex h-full items-center justify-center text-sm text-gray-400"
            >
              Loading institutional holders…
            </div>
          </foreignObject>
        );
      }

      for (const ref of quarterEndReferenceLines) {
        const x0 = xScale(ref.chartDate);
        if (x0 == null || Number.isNaN(Number(x0))) continue;
        const refCx = Number(x0) + (bw > 0 ? bw / 2 : 0);

        nodes.push(
          <line
            key={`inst-ref-${ref.quarterKey}`}
            x1={refCx}
            x2={refCx}
            y1={offset.top}
            y2={offset.top + offset.height}
            stroke="#cbd5e1"
            strokeWidth={1.5}
            strokeDasharray="4 4"
          />
        );
      }

      filingPeriods.forEach((period, index) => {
        const centerCx = columnCenterPlotX(
          xScale,
          bw,
          filingPeriods,
          index,
          visibleChartStartDate,
          quarterEndChartDatesAsc
        );
        if (centerCx == null) return;

        const slotWidth =
          quarterColumnSlotWidthPx(
            xScale,
            bw,
            filingPeriods,
            index,
            visibleChartStartDate,
            quarterEndChartDatesAsc
          ) ?? 0;
        slotWidths.push(slotWidth);

        const { densityMode, columnWidthPx, maxInstitutions } = layoutForQuarterColumn(slotWidth);

        nodes.push(
          <foreignObject
            key={`inst-col-${period.quarterKey}`}
            x={centerCx - columnWidthPx / 2}
            y={ribbonTop}
            width={columnWidthPx}
            height={ribbonHeight}
            className="overflow-hidden"
          >
            <div {...foreignObjectDivProps} className="h-full w-full bg-white">
              <InstitutionalHoldersColumn
                period={period}
                maxListHeight={listMaxH}
                holdersPayload={holdersPayload}
                stockSymbol={stockSymbol}
                displayMode={displayMode}
                searchHighlight={searchHighlight}
                searchFilterActive={searchFilterActive}
                densityMode={densityMode}
                maxInstitutions={maxInstitutions}
              />
            </div>
          </foreignObject>
        );
      });

      onOverlayMeta?.({ usesMinimalDensity: overlayUsesMinimalDensity(slotWidths) });

      if (nodes.length === 0) return null;

      return (
        <g className="recharts-layer institutional-holders-overlay" aria-hidden>
          {nodes}
        </g>
      );
    },
    [
      enabled,
      filingPeriods,
      quarterEndReferenceLines,
      holdersPayload,
      stockSymbol,
      visibleChartStartDate,
      loading,
      onOverlayMeta,
      displayMode,
      searchHighlight,
      searchFilterActive,
    ]
  );
}

/** @deprecated Use useInstitutionalHoldersChartOverlay */
export function useInstitutionalHoldersReferenceLines(
  enabled: boolean,
  filingPeriods: FilingPeriodForChart[],
  quarterEndReferenceLines: QuarterEndChartReference[] = [],
  holdersPayload: InstitutionalHoldersFileResponse | null = null,
  stockSymbol = '',
  visibleChartStartDate?: string | null,
  loading = false
) {
  return useInstitutionalHoldersChartOverlay(
    enabled,
    filingPeriods,
    quarterEndReferenceLines,
    holdersPayload,
    stockSymbol,
    visibleChartStartDate,
    loading
  );
}
